'use client';

import Link from 'next/link';
import { createPortal } from 'react-dom';
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import styles from './OverflowMenu.module.css';

/**
 * Menú de desborde: recoge los controles que dejaron de entrar en una barra.
 *
 * Existe porque en un teléfono no hay ancho para todo y esconder cosas no es
 * opción: las acciones del perfil propio —subir a ORCID, exportar, editar— y sus
 * pestañas privadas empujaban el nombre de la persona fuera de la pantalla. Acá
 * siguen accesibles, a un toque, sin competir por el ancho.
 *
 * Se usa SÓLO donde el ancho aprieta: en escritorio los mismos elementos se
 * muestran planos, que se leen de un vistazo y no cuestan un clic.
 */

export interface OverflowMenuItem {
	label: string;
	/** Enlace, o nada si el elemento está deshabilitado. */
	href?: string;
	onSelect?: () => void;
	Icon?: ComponentType<{ size?: number | string }>;
	disabled?: boolean;
	/** Insignia "Soon", igual que en las pestañas deshabilitadas. */
	soon?: boolean;
	active?: boolean;
}

interface OverflowMenuProps {
	items: OverflowMenuItem[];
	/** Qué recoge el menú. Es la etiqueta accesible: "⋯" a secas no dice nada. */
	label: string;
	/** Texto junto al icono. Sin él, el disparador queda cuadrado. */
	triggerLabel?: string;
	className?: string;
}

export function OverflowMenu({ items, label, triggerLabel, className = '' }: OverflowMenuProps) {
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState({ top: 0, right: 0 });
	const triggerRef = useRef<HTMLButtonElement>(null);

	// La posición se mide al abrir y se recalcula si la ventana cambia: el menú
	// vive en el `body`, así que no se mueve solo con su disparador.
	useEffect(() => {
		if (!open) return;
		const place = () => {
			const rect = triggerRef.current?.getBoundingClientRect();
			if (!rect) return;
			setPosition({ top: rect.bottom + 6, right: Math.max(12, window.innerWidth - rect.right) });
		};
		place();
		window.addEventListener('resize', place);
		window.addEventListener('scroll', place, true);
		return () => {
			window.removeEventListener('resize', place);
			window.removeEventListener('scroll', place, true);
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [open]);

	if (items.length === 0) return null;

	const renderItem = (item: OverflowMenuItem, index: number) => {
		const content: ReactNode = (
			<>
				{item.Icon ? <item.Icon size={14} aria-hidden="true" /> : null}
				<span className="truncate">{item.label}</span>
				{item.soon ? (
					<span className={styles.soon} aria-hidden="true">
						Soon
					</span>
				) : null}
			</>
		);

		if (item.disabled || (!item.href && !item.onSelect)) {
			return (
				<span key={index} className={`${styles.item} ${styles.itemDisabled}`} aria-disabled="true">
					{content}
				</span>
			);
		}

		if (item.href) {
			return (
				<Link
					key={index}
					href={item.href}
					role="menuitem"
					onClick={() => setOpen(false)}
					className={`${styles.item} ${item.active ? styles.itemActive : ''}`}
				>
					{content}
				</Link>
			);
		}

		return (
			<button
				key={index}
				type="button"
				role="menuitem"
				onClick={() => {
					item.onSelect?.();
					setOpen(false);
				}}
				className={`${styles.item} ${item.active ? styles.itemActive : ''}`}
			>
				{content}
			</button>
		);
	};

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => setOpen((value) => !value)}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={label}
				className={`${styles.trigger} ${open ? styles.triggerOpen : ''} ${className}`}
			>
				<MoreHorizontal size={16} aria-hidden="true" />
				{triggerLabel ? <span>{triggerLabel}</span> : null}
			</button>
			{open && typeof document !== 'undefined'
				? createPortal(
						<>
							<button
								type="button"
								className={styles.backdrop}
								aria-label={`Cerrar ${label}`}
								onClick={() => setOpen(false)}
							/>
							<div
								role="menu"
								aria-label={label}
								className={styles.menu}
								style={{ top: `${position.top}px`, right: `${position.right}px` }}
							>
								{items.map(renderItem)}
							</div>
						</>,
						document.body,
					)
				: null}
		</>
	);
}
